import taskService from "@/services/task.service";
import vrpService from "@/services/vrp.service";

// Coordinates geocoded (OpenStreetMap / Nominatim) from the addresses in
// IntegrationCompanyController. Keys match the values of `us.u_escritor`
// (Lisboa / Porto / Faro) and the PAT departments (SINTRA / MAIA).
const SINTRA = { name: "Sintra", lat: 38.7753, lng: -9.3341, address: "Rua do Alto do Forte nº5, 2635-244 Rio de Mouro" };
const MAIA = { name: "Maia", lat: 41.2732, lng: -8.6042, address: "R. Manuel Assunção Falcão 359 Pav. 14, 4475-041 Maia" };
const FARO = { name: "Faro", lat: 37.0179, lng: -7.9281, address: "Avenida 5 de Outubro 82 - 1D 8000-076 Faro" };

const OFFICE_BASES = { lisboa: SINTRA, sintra: SINTRA, porto: MAIA, maia: MAIA, faro: FARO };

const ROAD_FACTOR = 1.3;
const MAX_ROUTE_POINTS = 25; // keep in sync with VrpController::MAX_POINTS

/**
 * Resolve the technician's base from their office (u_escritor), then department.
 * Returns null when it cannot be determined, so we never show a distance
 * measured from the wrong base.
 */
export function getBaseLocation(user) {
  if (!user) return null;
  for (const field of [user.office, user.department]) {
    const base = OFFICE_BASES[String(field || "").trim().toLowerCase()];
    if (base) return { ...base };
  }
  return null;
}

export function toYmd(dateStr) {
  const parts = String(dateStr || "").split("-");
  return parts.length === 3 && parts[0].length === 2 ? parts.reverse().join("-") : dateStr;
}

function haversineMeters(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371e3 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function hasCoords(loc) {
  return !!(loc && Number(loc.latitude) && Number(loc.longitude));
}

/** Day's non-booking tasks for a user, sorted by start time. */
export async function getDayTasks(userId, date) {
  const ymd = toYmd(date);
  const days = (await taskService.getTasksAndBookings(userId, ymd, ymd)) || [];
  return days
    .filter((day) => day.day === ymd)
    .flatMap((day) => day.tasks || [])
    .filter((task) => !task.isBooking)
    .sort((a, b) => String(a.start || "").localeCompare(String(b.start || "")));
}

/**
 * Round trip Base -> stops (in the given order) -> Base.
 * stops: [{ lat, lng }]
 * Returns { km, polyline, isEstimate }. isEstimate = straight line x ROAD_FACTOR
 * (routing API unavailable or too many points).
 */
export async function calculateRoundTrip(base, stops) {
  const points = [base, ...stops, base].map(({ lat, lng }) => ({ lat: Number(lat), lng: Number(lng) }));

  if (points.length <= MAX_ROUTE_POINTS) {
    const route = await vrpService.getRoute(points.map((p) => [p.lat, p.lng]));
    if (route) {
      return { km: Math.round(route.distance_km), polyline: route.polyline || [], isEstimate: false };
    }
  }

  let meters = 0;
  for (let i = 0; i < points.length - 1; i++) meters += haversineMeters(points[i], points[i + 1]);
  return {
    km: Math.round((meters * ROAD_FACTOR) / 1000),
    polyline: points.map((p) => [p.lat, p.lng]),
    isEstimate: true,
  };
}
